import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import type { FeedbackPriority } from "@/lib/feedback-priority";
import {
  buildFeedbackAttachmentPath,
  FEEDBACK_ALLOWED_MIME_TYPES,
  FEEDBACK_MAX_ATTACHMENTS,
  FEEDBACK_MAX_ATTACHMENT_BYTES,
  validateFeedbackAttachments,
} from "@/lib/feedback-attachments";

export type FeedbackType = "bug" | "feature";

export type { FeedbackPriority } from "@/lib/feedback-priority";

// Re-eksport for bakoverkompatibilitet — eksterne callere har importert
// disse fra `use-feedback`. Selve definisjonene lever i lib/ så vi kan
// unit-teste uten å dra inn supabase-klienten.
export {
  FEEDBACK_ALLOWED_MIME_TYPES,
  FEEDBACK_MAX_ATTACHMENTS,
  FEEDBACK_MAX_ATTACHMENT_BYTES,
  validateFeedbackAttachments,
};

interface SendFeedbackInput {
  /** activeUser.id — den brukeren feedbacken skal lagres på (kan være impersonert) */
  userId: string;
  type: FeedbackType;
  /** VIB-2085: hvor hardt saken rammer avsenderen. Påkrevd i UI. */
  priority: FeedbackPriority;
  body: string;
  pageUrl: string;
  /**
   * Hvis admin impersonerer en annen bruker, send den FAKTISKE innloggede
   * admin-brukerens id her. Edge function-en validerer at den matcher JWT.
   * `null`/undefined = ikke impersonering.
   */
  impersonatedByUserId?: string | null;
  /** Valgfrie filer som lastes opp til `feedback-attachments`-bucket før Slack-postingen. */
  attachments?: File[];
}

interface SendFeedbackResponse {
  ok: boolean;
  feedback_id: string;
  slack_ok: boolean;
  slack_error: string | null;
}

/**
 * Sender feedback til `send-feedback` edge function. Edge function-en
 * inserter raden i `public.feedback` og videresender en Slack-melding til
 * #solvrev-feedback (best effort — Slack-feil krasher ikke insertet).
 *
 * Vedlegg lastes opp klient-side til `feedback-attachments`-bucket FØR vi
 * kaller edge function-en, slik at edge function-en kun trenger å holde
 * styr på path-array-en, ikke selve filene. Hvis opplastingen feiler,
 * stopper vi før Slack-melding sendes.
 *
 * Hvorfor ikke direkte Supabase-insert?
 *   • Vi vil ha én sannhetskilde for "feedback opprettet → Slack varslet".
 *   • Slack webhook-URL skal IKKE eksponeres i frontend-bundlen.
 *   • Edge function validerer impersonering server-side (admin-only).
 *   • Signed URLs til private vedlegg krever service_role — må skje server-side.
 */
export function useSendFeedback() {
  return useMutation({
    mutationFn: async (input: SendFeedbackInput) => {
      const files = input.attachments ?? [];
      const validationError = validateFeedbackAttachments(files);
      if (validationError) {
        throw new Error(validationError);
      }

      // 1. Last opp filer sekvensielt. Sekvensielt er trygt (10 filer x 20MB
      //    er ikke tidskritisk) og enklere å diagnose ved feil.
      const attachmentPaths: string[] = [];
      for (const file of files) {
        const path = buildFeedbackAttachmentPath(input.userId, file);
        const { error: uploadErr } = await supabase.storage
          .from("feedback-attachments")
          .upload(path, file, {
            contentType: file.type,
            upsert: false,
          });
        if (uploadErr) {
          throw new Error(
            `Opplasting av ${file.name} feilet: ${uploadErr.message}`,
          );
        }
        attachmentPaths.push(path);
      }

      // 2. Kall edge function. Sender attachment_paths med så den kan
      //    persistere på raden og generere signed URLs til Slack.
      const { data, error } = await supabase.functions.invoke<SendFeedbackResponse>(
        "send-feedback",
        {
          body: {
            user_id: input.userId,
            type: input.type,
            priority: input.priority,
            body: input.body.trim(),
            page_url: input.pageUrl,
            impersonated_by_user_id: input.impersonatedByUserId ?? null,
            attachment_paths: attachmentPaths,
          },
        },
      );
      if (error) throw error;
      if (!data?.ok) {
        throw new Error("Feedback kunne ikke lagres");
      }
      return data;
    },
    onSuccess: (data) => {
      // Edge function-en inserter raden FØRST og prøver Slack BEST EFFORT.
      // Hvis Slack-postingen feilet (manglende webhook, ugyldig blocks, osv.)
      // får vi `slack_error` i responsen — vis warning så Erik ser problemet
      // umiddelbart i UI istedenfor å måtte grave i edge-loggene.
      if (data?.slack_error) {
        toast.warning("Lagret, men Slack-melding feilet", {
          description: data.slack_error,
        });
      } else {
        toast.success("Takk for tilbakemeldingen!");
      }
    },
    onError: (err) => {
      toast.error(`Kunne ikke sende: ${(err as Error).message}`);
    },
  });
}
